/**
 * nativeVersionGate — the forced native-update check, run once at cold start.
 *
 * Some changes cannot ship as an OTA bundle: a new minimum OS version, a
 * WebView or runtime behaviour change, a Capacitor or native plugin upgrade,
 * a new permission, a store-mandated target SDK bump. When one of those lands,
 * a player on an older native shell needs a new build from the store, and
 * until they take it their app may not work at all. This tells them, instead
 * of leaving them on a broken app with no explanation.
 *
 * How it decides:
 *
 *   1. Read the installed APP (native shell) version locally — App.getInfo(),
 *      no network, nothing sent anywhere.
 *   2. Fetch a published document holding one minimum supported version per
 *      platform. It is a plain read of a static file: no request payload, no
 *      per-player evaluation, nothing about this device leaves the app.
 *   3. Block if, and only if, the installed version is strictly older than
 *      that floor.
 *
 * Everything else lets the player through. That bias is deliberate and load
 * bearing: this is the one piece of client code that can make the app
 * unusable, so every unknown — no network, a slow CDN, a malformed document,
 * a schema we do not recognise, a version string we cannot order — resolves
 * to "not blocked". A false block is far worse than a missed one, because a
 * missed block still leaves the player with the app they had.
 *
 * Cold start only. The check never runs on resume, so raising the floor while
 * someone is at a table cannot interrupt them; they see it on their next cold
 * start. That is why this is started once at module scope rather than from a
 * lifecycle listener.
 */

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { isComparableVersion, isOlderThan } from 'utils/compareVersions';

/**
 * The only schema this client understands. The document carries its own
 * schema number so an incompatible future shape is REJECTED rather than
 * misread — a document we half-understand could block the wrong people.
 */
export const SUPPORTED_SCHEMA = 1;

/**
 * Hard cap on the read. Short on purpose: this sits in front of the lobby on
 * every cold start, and a slow CDN must cost the player a moment, not a boot.
 */
export const FETCH_TIMEOUT_MS = 1500;

export type VersionPolicy = {
  schema: number;
  minimumSupported?: Partial<Record<'ios' | 'android', string>>;
  downloadUrl?: Partial<Record<'ios' | 'android', string>>;
};

export type VersionGateResult =
  | {
      blocked: true;
      /** The installed native shell version. */
      installed: string;
      /** The floor it failed to meet. */
      required: string;
      platform: string;
      /** Where to get the new build. Always set: see WEBSITE_DOWNLOAD_URL. */
      downloadUrl: string;
    }
  | {
      blocked: false;
      /** Why we are letting them through — for logs and analytics only. */
      reason:
        | 'web' // no native shell to gate
        | 'no-native-version' // could not read the installed version
        | 'unreachable' // offline, timed out, or the fetch failed
        | 'unreadable' // not JSON (see the note on 404s below) or wrong shape
        | 'unknown-schema' // a document written for a newer client
        | 'no-floor' // no floor published for this platform
        | 'unorderable' // a version string we cannot compare
        | 'supported'; // genuinely up to date
    };

/**
 * Fallback download links, so the block screen always has a working button
 * even if whoever raised the floor did not supply one. The published document can
 * still override these (a changed listing, a regional link) without a client
 * release, which is the point of it carrying them at all.
 *
 * Android is NOT the Play Store: the app is distributed as a direct APK from
 * the website, so "update" means sending the player to the download page
 * rather than a store listing. That is also why the block screen opens these
 * with the in-app browser instead of the plugin's openAppStore(), which only
 * knows how to reach the two official stores.
 */
const DEFAULT_DOWNLOAD_URL: Record<string, string> = {
  ios: 'https://apps.apple.com/us/app/hijack-poker/id6464543286',
  android: 'https://www.hijackpoker.com/'
};

/**
 * Last-resort link, used only if a native platform ever appears that has no
 * entry above. The website carries builds for every platform, so it is always
 * a real destination.
 *
 * This exists so a blocked result ALWAYS carries a usable link. The block
 * screen is terminal, so a missing link would be a genuine dead end for the
 * player; guaranteeing one here means the screen never has to handle that
 * case, and the type says so.
 */
const WEBSITE_DOWNLOAD_URL = 'https://www.hijackpoker.com/';

/**
 * Where the floor is published. Same bucket and CloudFront distribution as
 * the OTA manifest, so there is no new infrastructure to keep alive.
 *
 * Built from REACT_APP_HARDWARE_VERSION_DOMAIN, NOT REACT_APP_DOMAIN. The build
 * workflows set it straight from the GitHub Environment's DOMAIN variable,
 * the same value set-minimum-app-version.yml publishes to, so the host the
 * client reads always matches the host the floor is written to.
 * REACT_APP_DOMAIN does not: production overrides it to hijack.poker (engine
 * websocket and auth), so the client asked app.hijack.poker, a host that does
 * not resolve, and every production read failed open (EN-11029).
 *
 * Falls back to REACT_APP_DOMAIN, which is correct on every environment except
 * production, so a build made before its workflow set the variable behaves
 * exactly as it did before.
 */
export const policyUrl = (): string | null => {
  const domain = (process.env.REACT_APP_HARDWARE_VERSION_DOMAIN || process.env.REACT_APP_DOMAIN || '')
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
  return domain ? `https://app.${domain}/app-version.json` : null;
};

export type VersionGateDeps = {
  isNative: () => boolean;
  getPlatform: () => string;
  getInfo: () => Promise<{ version: string }>;
  /** Reads the document. Resolves to the raw body, or null on any failure. */
  read: (url: string) => Promise<string | null>;
  url: () => string | null;
};

/**
 * Read the document over NATIVE http rather than the WebView's fetch.
 *
 * The WebView reports its origin as https://hijack.poker (the hostname spoof
 * in capacitor.config.ts, there for WebOTP), while the document is served
 * from app.<domain>. A JS fetch would therefore be a cross-origin request and
 * need CORS headers on the CDN. CapacitorHttp issues the request from native
 * code — the same layer the Capgo plugin already uses for the OTA manifest,
 * which is why that has never needed CORS either — so the same-origin policy
 * does not apply and no CDN or bucket change is required.
 *
 * responseType 'text' on purpose: 'json' would let the native layer parse,
 * and we want to do it ourselves. The distribution rewrites 403/404 to
 * /index.html with a 200, so a MISSING document arrives as HTML with a
 * success status, and an explicit parse is what turns that into "no policy".
 */
const readNative = async (url: string): Promise<string | null> => {
  try {
    const response = await CapacitorHttp.get({
      url,
      responseType: 'text',
      connectTimeout: FETCH_TIMEOUT_MS,
      readTimeout: FETCH_TIMEOUT_MS
    });
    if (response.status < 200 || response.status >= 300) return null;
    return typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
  } catch {
    return null;
  }
};

const defaultDeps: VersionGateDeps = {
  isNative: () => Capacitor.isNativePlatform(),
  getPlatform: () => Capacitor.getPlatform(),
  getInfo: () => CapacitorApp.getInfo(),
  read: readNative,
  url: policyUrl
};

/**
 * Accept a published download link only if it is a non-empty https string.
 *
 * The document is external input to the client, and this URL is handed to the
 * only screen a blocked player can act on. A non-string (an object, say) would
 * be rendered straight into the fallback, and a non-https value would be
 * handed to the system browser. Anything unexpected falls back to the
 * platform default, which is always a working link.
 */
const usableDownloadUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value) return undefined;
  return value.startsWith('https://') ? value : undefined;
};

const log = (message: string, ...rest: unknown[]) =>
  console.log(`[VersionGate] ${message}`, ...rest);

const pass = (
  reason: Extract<VersionGateResult, { blocked: false }>['reason']
): VersionGateResult => {
  log(`not blocked (${reason})`);
  return { blocked: false, reason };
};

const fetchPolicy = async (deps: VersionGateDeps): Promise<VersionPolicy | null> => {
  const url = deps.url();
  if (!url) return null;

  // CapacitorHttp's own connect/read timeouts are the primary cap; this race
  // is the backstop for a request that somehow honours neither, because the
  // one thing this must never do is hang the launch.
  let timer: ReturnType<typeof setTimeout>;
  const body = await Promise.race([
    // readNative never rejects, but `read` is an injected seam and this
    // function is the documented fail-open boundary, so it cannot depend on
    // the caller's implementation being well behaved.
    deps.read(url).catch(() => null),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), FETCH_TIMEOUT_MS + 250);
    })
  ]).finally(() => clearTimeout(timer));

  if (!body) return null;
  try {
    return JSON.parse(body) as VersionPolicy;
  } catch {
    return null;
  }
};

export const evaluateVersionGate = async (
  deps: VersionGateDeps = defaultDeps
): Promise<VersionGateResult> => {
  if (!deps.isNative()) return pass('web');

  const platform = deps.getPlatform();
  const installed = await deps
    .getInfo()
    .then((info) => info.version)
    .catch(() => null);

  if (!installed) return pass('no-native-version');

  const policy = await fetchPolicy(deps);
  if (!policy) return pass('unreachable');
  // `JSON.parse('null')` and `JSON.parse('[]')` both succeed, so a parseable
  // body is not necessarily a document. Check the shape explicitly rather
  // than leaning on the falsy check above.
  if (typeof policy !== 'object' || Array.isArray(policy) || typeof policy.schema !== 'number') {
    return pass('unreadable');
  }
  if (policy.schema !== SUPPORTED_SCHEMA) {
    log(`document schema ${policy.schema}, this client understands ${SUPPORTED_SCHEMA}`);
    return pass('unknown-schema');
  }

  const required = policy.minimumSupported?.[platform as 'ios' | 'android'];
  if (!required) return pass('no-floor');
  if (!isComparableVersion(required) || !isComparableVersion(installed)) {
    log(`cannot order installed=${installed} against required=${required}`);
    return pass('unorderable');
  }

  if (!isOlderThan(installed, required)) return pass('supported');

  log(`blocked: installed ${installed} is older than the minimum ${required}`);
  return {
    blocked: true,
    installed,
    required,
    platform,
    downloadUrl:
      usableDownloadUrl(policy.downloadUrl?.[platform as 'ios' | 'android']) ??
      DEFAULT_DOWNLOAD_URL[platform] ??
      WEBSITE_DOWNLOAD_URL
  };
};

let running: Promise<VersionGateResult> | null = null;

/**
 * Run the check once per app start. Idempotent, so index.tsx can begin it at
 * module scope (overlapping React boot) and the gate component can await the
 * same work rather than issuing a second request.
 */
export const startVersionGate = (
  deps: VersionGateDeps = defaultDeps
): Promise<VersionGateResult> => {
  if (!running) {
    running = evaluateVersionGate(deps).catch((err): VersionGateResult => {
      // Belt and braces: evaluateVersionGate already swallows its own
      // failures, but an unexpected throw here must still let the player in.
      log('unexpected failure; not blocking', err);
      return { blocked: false, reason: 'unreachable' };
    });
  }
  return running;
};

/** Test-only: clear the once-per-start memo. */
export const resetVersionGateForTests = () => {
  running = null;
};
